// ABOUTME: Hooks for uploading account files via direct GCS upload.
// ABOUTME: Three-step flow: presign → upload to GCS → confirm.

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { rqClient } from "@/shared/api/instance";
import type { ApiSchemas } from "@/shared/api/schema";
import { CONFIG } from "@/shared/model/config";
import { useSession } from "@/shared/model/session";

export type Product = ApiSchemas["Product"];

export type AccountFileUploadResponse = {
  message: string;
  upload_id: string;
  product: Product;
};

type PresignResponse = {
  url: string;
  key: string;
};

async function fetchWithAuth<T>(
  url: string,
  options: RequestInit = {},
): Promise<T> {
  const authHeaders = useSession.getState().getAuthHeaders();
  if (!authHeaders) {
    throw new Error("Not authenticated");
  }

  const response = await fetch(url, {
    ...options,
    headers: {
      ...authHeaders,
      ...options.headers,
    },
    credentials: "include",
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ error: { message: "Request failed" } }));
    throw new Error(error.error?.message || "Request failed");
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

export function useUploadAccountFiles() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      productId,
      file,
    }: {
      productId: string;
      file: File;
    }): Promise<AccountFileUploadResponse> => {
      // Step 1: Get presigned URL from backend
      const presign = await fetchWithAuth<PresignResponse>(
        `${CONFIG.API_BASE_URL}/api/v1/products/${productId}/account_files/presign`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            filename: file.name,
            content_type: "application/zip",
          }),
        },
      );

      // Step 2: Upload file directly to GCS
      const uploadResponse = await fetch(presign.url, {
        method: "PUT",
        headers: { "Content-Type": "application/zip" },
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error("Failed to upload file to storage");
      }

      // Step 3: Confirm upload with backend
      return fetchWithAuth<AccountFileUploadResponse>(
        `${CONFIG.API_BASE_URL}/api/v1/products/${productId}/account_files/confirm`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ key: presign.key }),
        },
      );
    },
    onSettled: async (_, __, { productId }) => {
      await queryClient.invalidateQueries(
        rqClient.queryOptions("get", "/api/v1/products/{id}", {
          params: { path: { id: productId } },
        }),
      );
      await queryClient.invalidateQueries(
        rqClient.queryOptions("get", "/api/v1/seller/products", {}),
      );
      await queryClient.invalidateQueries(
        rqClient.queryOptions("get", "/api/v1/seller/products/stats", {}),
      );
      await queryClient.invalidateQueries(
        rqClient.queryOptions("get", "/api/v1/stores/me", {}),
      );
    },
  });
}
